export interface AnalyticsRoute {
  pathname: string
  path: string
  location: string
  referrer: string
  revision: number
}

// How long a navigation waits for a redirect before it counts. Routers redirect
// by replacing the entry from an effect (/requests → /requests/sent), and that
// bounce is one page view, not two.
export const ANALYTICS_NAVIGATION_SETTLE_MS = 500

// Query changes and ordinary anchors retain the existing page-view semantics.
// Hash routers (/#/screen) expose an actual page; hash query filters do not.
// `location` feeds GA's page_location/page_referrer, so it never carries the
// query: auth links put tokens there (/login/withToken?token=…) and GA only
// redacts email addresses.
export const readAnalyticsRoute = (
  href: string,
  referrer = '',
  revision = 0
): AnalyticsRoute => {
  const url = new URL(href)
  const routeHash = url.hash.startsWith('#/') ? url.hash.split('?')[0] : ''
  const path = url.pathname + routeHash
  return {
    pathname: url.pathname,
    path,
    location: url.origin + path,
    referrer,
    revision
  }
}

export interface AnalyticsNavigationObserver {
  getSnapshot(): AnalyticsRoute
  subscribe(listener: () => void): () => void
  // True from the history change until the new path is counted (or dropped).
  isNavigating(): boolean
}

// An iframe's WindowProxy survives same-origin navigation but then forwards to
// the next document's Window, so each observer belongs to one document. Keyed
// by that document, an entry never outlives it, even while the frame stays.
const observers = new WeakMap<Document, AnalyticsNavigationObserver>()

export const getAnalyticsNavigation = (
  target: Window = window
): AnalyticsNavigationObserver => {
  const observedDocument = target.document
  const existing = observers.get(observedDocument)
  if (existing) {
    existing.getSnapshot()
    return existing
  }

  let initialReferrer = ''
  try {
    if (target.document.referrer)
      initialReferrer = readAnalyticsRoute(target.document.referrer).location
  } catch {
    /* A malformed referrer is not usable analytics context. */
  }
  let snapshot = readAnalyticsRoute(target.location.href, initialReferrer)
  const listeners = new Set<() => void>()
  let disconnect: (() => void) | undefined
  let navigating = () => false

  const start = () => {
    let active = true
    // Changes within one task are one step, read at its final URL: a blocked
    // Back operation pushes the original URL back synchronously.
    let stepTimer: number | undefined
    let stepNavigated = false
    // A navigation waits before it counts. A replace-only step in that window
    // is its redirect and folds into it; a push, Back/Forward or hash change is
    // a navigation of its own, so even a short visit (A → B → A) keeps its view.
    let settlingHref: string | undefined
    let settleTimer: number | undefined
    const history = target.history
    const originalPush = history.pushState
    const originalReplace = history.replaceState
    const commit = () => {
      target.clearTimeout(settleTimer)
      settleTimer = undefined
      const href = settlingHref
      settlingHref = undefined
      if (!active || href === undefined) return
      const next = readAnalyticsRoute(
        href,
        snapshot.location,
        snapshot.revision + 1
      )
      if (next.path === snapshot.path) return
      snapshot = next
      listeners.forEach(listener => listener())
    }
    const endStep = () => {
      stepTimer = undefined
      const navigated = stepNavigated
      stepNavigated = false
      if (!active) return
      const href = target.location.href
      const { path } = readAnalyticsRoute(href)
      if (settlingHref !== undefined) {
        if (path === readAnalyticsRoute(settlingHref).path) return
        if (!navigated) {
          settlingHref = href
          return
        }
        commit()
      }
      if (path === snapshot.path) return
      settlingHref = href
      try {
        settleTimer = target.setTimeout(commit, ANALYTICS_NAVIGATION_SETTLE_MS)
      } catch {
        /* A departing frame may no longer accept timers. */
      }
    }
    const schedule = (navigated: boolean) => {
      if (!active) return
      if (navigated) stepNavigated = true
      if (stepTimer !== undefined) return
      try {
        stepTimer = target.setTimeout(endStep, 0)
      } catch {
        /* A departing frame may no longer accept timers. */
      }
    }
    const onNavigation = () => schedule(true)
    // Synchronous, so a bridge message sent right after the history call
    // already sees it. A query-only rewrite doesn't count as navigating.
    navigating = () => {
      if (settlingHref !== undefined) return true
      if (stepTimer === undefined) return false
      try {
        return readAnalyticsRoute(target.location.href).path !== snapshot.path
      } catch {
        return false // A frame now on another origin has no URL to compare.
      }
    }
    const push: History['pushState'] = function (this: History, ...args) {
      const result = originalPush.apply(this, args)
      schedule(true)
      return result
    }
    const replace: History['replaceState'] = function (this: History, ...args) {
      const result = originalReplace.apply(this, args)
      schedule(false)
      return result
    }

    try {
      history.pushState = push
      history.replaceState = replace
    } catch {
      // A locked-down History object must not prevent the application loading.
      if (history.pushState === push) history.pushState = originalPush
    }
    target.addEventListener('popstate', onNavigation)
    target.addEventListener('hashchange', onNavigation)
    // Covers navigation between render and subscription without polling.
    schedule(true)
    return () => {
      active = false
      navigating = () => false
      // After navigation `target` reaches the next document's Window, where
      // these timer ids may belong to its own timers. The old document took
      // its timers and listeners with it, and `active` is off. A frame that
      // navigated to another origin throws here for the same reason.
      try {
        if (target.document === observedDocument) {
          target.clearTimeout(stepTimer)
          target.clearTimeout(settleTimer)
          target.removeEventListener('popstate', onNavigation)
          target.removeEventListener('hashchange', onNavigation)
        }
      } catch {
        /* Nothing left to remove across origins. */
      }
      // Another library may have wrapped us since installation. Leave its
      // wrapper intact; our retained wrapper becomes an inert pass-through.
      try {
        if (history.pushState === push) history.pushState = originalPush
        if (history.replaceState === replace)
          history.replaceState = originalReplace
      } catch {
        /* A third party may have locked History after setup. */
      }
    }
  }

  const observer: AnalyticsNavigationObserver = {
    getSnapshot: () => {
      if (!listeners.size) {
        const next = readAnalyticsRoute(
          target.location.href,
          snapshot.location,
          snapshot.revision + 1
        )
        if (next.path !== snapshot.path) snapshot = next
      }
      return snapshot
    },
    subscribe: listener => {
      listeners.add(listener)
      if (listeners.size === 1) disconnect = start()
      return () => {
        listeners.delete(listener)
        if (listeners.size === 0) {
          disconnect?.()
          disconnect = undefined
        }
      }
    },
    isNavigating: () => navigating()
  }
  observers.set(observedDocument, observer)
  return observer
}
